import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { GalleryImage } from '../types/media';
interface PhotoLightboxProps {
  images: GalleryImage[];
  activeIndex: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
}
export function PhotoLightbox({
  images,
  activeIndex,
  onClose,
  onChange
}: PhotoLightboxProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  useEffect(() => {
    if (activeIndex === null) return;
    previouslyFocused.current = document.activeElement as HTMLElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onChange((activeIndex - 1 + images.length) % images.length);
      if (event.key === 'ArrowRight') onChange((activeIndex + 1) % images.length);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [activeIndex, images.length, onChange, onClose]);
  const image = activeIndex === null ? null : images[activeIndex];
  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };
  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null || activeIndex === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta > 0) onChange((activeIndex - 1 + images.length) % images.length);else onChange((activeIndex + 1) % images.length);
    }
    touchStartX.current = null;
  };
  return <AnimatePresence data-id="element-140" data-mp="2hqu2bhzh1hga">
      {image && activeIndex !== null && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-4 backdrop-blur-md sm:p-8" role="dialog" aria-modal="true" aria-label={`Photograph ${activeIndex + 1} of ${images.length}`} onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-141" data-mp="2lp9nrhfwmhrl">
          <button ref={closeButtonRef} type="button" onClick={onClose} className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-led/30 bg-ink/60 text-led transition-colors duration-300 hover:border-ice focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice sm:right-8 sm:top-8" aria-label="Close gallery" data-id="element-142" data-mp="1tzy4a34f9q0l">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-143" data-mp="b40kvmpk7sxf" />
          </button>

          <button type="button" onClick={() => onChange((activeIndex - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-led/25 bg-ink/50 text-led transition-colors duration-300 hover:border-ice focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice sm:left-6" aria-label="Previous photograph" data-id="element-144" data-mp="b64ywxh6a8bf">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-145" data-mp="q1t2qlikwtes" />
          </button>
          <button type="button" onClick={() => onChange((activeIndex + 1) % images.length)} className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-led/25 bg-ink/50 text-led transition-colors duration-300 hover:border-ice focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice sm:right-6" aria-label="Next photograph" data-id="element-146" data-mp="2vv13w0nmeohr">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-147" data-mp="37a4dslzqpg20" />
          </button>

          <motion.img key={image.src} initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} src={image.src} alt={image.alt} className="max-h-[78vh] max-w-full object-contain" data-id="element-148" data-mp="2ajlq1ilbq793" />

          {(image.caption || image.credit) && <div className="absolute inset-x-5 bottom-5 text-center sm:inset-x-8 sm:bottom-8" data-id="element-149" data-mp="1qvmbrxrjwhyx">
              {image.caption && <p className="text-sm text-led/85" data-id="element-150" data-mp="1vtxpwkovpemy">{image.caption}</p>}
              {image.credit && <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-led/45" data-id="element-151" data-mp="1ob6k695d6wfu">{image.credit}</p>}
            </div>}
        </motion.div>}
    </AnimatePresence>;
}